import React, { useMemo, useState, useCallback, useEffect } from 'react';
import {
  Box, Typography, Card, CardContent, useTheme, CircularProgress,
  Chip, Stack, Breadcrumbs, Link, Button, Paper, Grid, FormControl,
  InputLabel, Select, MenuItem, IconButton, Slider, Tooltip as MuiTooltip
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import HomeIcon from '@mui/icons-material/Home';
import ZoomInIcon from '@mui/icons-material/ZoomIn';
import ZoomOutIcon from '@mui/icons-material/ZoomOut';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, ReferenceLine
} from 'recharts';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5001';

// Paleta de 10 cores distintas
const COLORS = [
  '#1f77b4', '#ff7f0e', '#2ca02c', '#d62728', '#9467bd',
  '#8c564b', '#e377c2', '#7f7f7f', '#bcbd22', '#17becf'
];

// Configuracao dos niveis de drill-down
const DRILL_LEVELS = {
  1: { name: 'Contrato', field: 'des_contrato', endpoint: '/historico-mensal-performance' },
  2: { name: 'Centro de Custo', field: 'des_centro_servico', endpoint: '/historico-mensal-centro' },
  3: { name: 'Equipe', field: 'des_equipe', endpoint: '/historico-mensal-equipe' },
  4: { name: 'Historico Diario', field: 'dta', endpoint: '/historico-diario-equipe', isLeaf: true }
};

// Opcoes de indicadores
const INDICATORS = [
  { key: 'percentual_faturamento', label: '% Faturamento', yAxisLabel: '%' },
  { key: 'percentual_turnos', label: '% Turnos Realizados', yAxisLabel: '%' },
  { key: 'tempo_medio_saida_base', label: 'Tempo Saida Base', yAxisLabel: 'min' }
];

const DrillDownChart = ({ height = 400 }) => {
  const theme = useTheme();

  // Estado do drill-down
  const [drillPath, setDrillPath] = useState([{ level: 1, value: null, label: 'Todos Contratos' }]);
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [hiddenItems, setHiddenItems] = useState({});
  const [selectedIndicator, setSelectedIndicator] = useState('percentual_faturamento');

  // Estado de zoom (para nivel diario)
  const [zoomLevel, setZoomLevel] = useState(100); // 100% = normal, 200% = 2x largura

  // Nivel atual
  const currentLevel = drillPath[drillPath.length - 1].level;
  const levelConfig = DRILL_LEVELS[currentLevel];
  const indicatorConfig = INDICATORS.find(i => i.key === selectedIndicator);

  // Reset zoom quando muda de nivel
  useEffect(() => {
    setZoomLevel(100);
  }, [currentLevel]);

  // Handlers de zoom
  const handleZoomIn = () => setZoomLevel(prev => Math.min(prev + 50, 300));
  const handleZoomOut = () => setZoomLevel(prev => Math.max(prev - 50, 100));
  const handleZoomReset = () => setZoomLevel(100);

  // Buscar dados quando drill path muda
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      setError(null);
      setHiddenItems({});

      try {
        const token = localStorage.getItem('access_token');
        const headers = {
          'Content-Type': 'application/json'
        };
        if (token) {
          headers['Authorization'] = `Bearer ${token}`;
        }

        // Construir filtros baseado no drill path
        const filters = {};
        drillPath.forEach(item => {
          if (item.level === 2 && item.parentValue) {
            filters.des_contrato = item.parentValue;
          }
          if (item.level === 3 && item.parentValue) {
            filters.des_centro_servico = item.parentValue;
          }
          if (item.level === 4 && item.parentValue) {
            filters.des_equipe = item.parentValue;
          }
        });

        // Adicionar filtros dos niveis anteriores
        if (currentLevel >= 2) {
          const contratoItem = drillPath.find(p => p.level === 2);
          if (contratoItem && contratoItem.parentValue) {
            filters.des_contrato = contratoItem.parentValue;
          }
        }
        if (currentLevel >= 3) {
          const centroItem = drillPath.find(p => p.level === 3);
          if (centroItem && centroItem.parentValue) {
            filters.des_centro_servico = centroItem.parentValue;
          }
        }
        if (currentLevel >= 4) {
          const equipeItem = drillPath.find(p => p.level === 4);
          if (equipeItem && equipeItem.parentValue) {
            filters.des_equipe = equipeItem.parentValue;
          }
        }

        const response = await fetch(`${API_URL}${levelConfig.endpoint}`, {
          method: 'POST',
          headers,
          body: JSON.stringify(filters)
        });

        if (!response.ok) {
          throw new Error(`Erro ${response.status}: ${response.statusText}`);
        }

        const result = await response.json();
        setData(Array.isArray(result) ? result : []);
      } catch (err) {
        console.error('Erro ao buscar dados:', err);
        setError(err.message);
        setData([]);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [drillPath, currentLevel, levelConfig.endpoint]);

  // Processar dados para o grafico
  const { chartData, items, colorMap } = useMemo(() => {
    if (!data || data.length === 0) {
      return { chartData: [], items: [], colorMap: {} };
    }

    const field = levelConfig.field;
    const isDaily = levelConfig.isLeaf;

    // Extrair lista unica de items (contratos, centros, equipes ou datas)
    const itemsSet = new Set();
    data.forEach(item => {
      const value = item[field];
      if (value) {
        itemsSet.add(value);
      }
    });
    const itemsArray = Array.from(itemsSet).sort();

    // Criar mapa de cores
    const colors = {};
    itemsArray.forEach((item, index) => {
      colors[item] = COLORS[index % COLORS.length];
    });

    if (isDaily) {
      // Para nivel diario, eixo X sao as datas
      return {
        chartData: data.map(item => ({
          ...item,
          name: item.dta_label || item.dta
        })),
        items: ['Equipe'],
        colorMap: { 'Equipe': COLORS[0] }
      };
    }

    // Para outros niveis, agrupar por mes
    const mesMap = {};
    data.forEach(item => {
      const mesLabel = item.mes_label || item.mes;
      if (!mesMap[mesLabel]) {
        mesMap[mesLabel] = { mes_label: mesLabel, mes: item.mes };
      }
      const itemValue = item[field];
      if (itemValue && item[selectedIndicator] !== undefined) {
        mesMap[mesLabel][itemValue] = Number(item[selectedIndicator]) || 0;
      }
    });

    // Ordenar por data
    const sortedData = Object.values(mesMap).sort((a, b) => {
      if (a.mes && b.mes) {
        return new Date(a.mes) - new Date(b.mes);
      }
      return 0;
    });

    return {
      chartData: sortedData,
      items: itemsArray,
      colorMap: colors
    };
  }, [data, levelConfig, selectedIndicator]);

  // Calcular media para nivel diario
  const averageValue = useMemo(() => {
    if (!levelConfig.isLeaf || !chartData || chartData.length === 0) {
      return null;
    }

    const values = chartData
      .map(item => Number(item[selectedIndicator]))
      .filter(val => !isNaN(val) && val !== null && val !== undefined);

    if (values.length === 0) return null;

    const sum = values.reduce((acc, val) => acc + val, 0);
    return sum / values.length;
  }, [chartData, selectedIndicator, levelConfig.isLeaf]);

  // Handler para drill-down (clicar em um item)
  const handleDrillDown = useCallback((itemValue) => {
    if (levelConfig.isLeaf) return; // Nao pode ir mais fundo

    const nextLevel = currentLevel + 1;

    setDrillPath(prev => [
      ...prev,
      {
        level: nextLevel,
        value: itemValue,
        label: itemValue,
        parentValue: itemValue
      }
    ]);
  }, [currentLevel, levelConfig.isLeaf]);

  // Handler para voltar um nivel
  const handleBack = useCallback(() => {
    if (drillPath.length > 1) {
      setDrillPath(prev => prev.slice(0, -1));
    }
  }, [drillPath.length]);

  // Handler para ir para um nivel especifico (via breadcrumb)
  const handleBreadcrumbClick = useCallback((index) => {
    setDrillPath(prev => prev.slice(0, index + 1));
  }, []);

  // Toggle visibilidade de item na legenda
  const handleLegendClick = (item) => {
    if (levelConfig.isLeaf) return;
    setHiddenItems(prev => ({
      ...prev,
      [item]: !prev[item]
    }));
  };

  // Tooltip customizado
  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      const sortedPayload = [...payload].sort((a, b) => (b.value || 0) - (a.value || 0));
      const isDaily = levelConfig.isLeaf;

      return (
        <Box
          sx={{
            backgroundColor: theme.palette.background.paper,
            border: `1px solid ${theme.palette.divider}`,
            borderRadius: 1,
            p: 2,
            boxShadow: theme.shadows[4],
            maxHeight: 350,
            overflowY: 'auto'
          }}
        >
          <Typography
            variant={isDaily ? 'h6' : 'subtitle1'}
            sx={{ fontWeight: 600, mb: 1.5, color: theme.palette.text.primary }}
          >
            {label}
          </Typography>
          {sortedPayload.map((entry, index) => (
            <Box key={index} sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 0.75 }}>
              <Box
                sx={{
                  width: isDaily ? 16 : 12,
                  height: isDaily ? 16 : 12,
                  borderRadius: '50%',
                  backgroundColor: entry.color,
                  flexShrink: 0
                }}
              />
              <Typography
                variant={isDaily ? 'body1' : 'body2'}
                sx={{ color: theme.palette.text.secondary, fontSize: isDaily ? '1rem' : '0.9rem' }}
              >
                {entry.name}: <strong style={{ fontSize: isDaily ? '1.15rem' : '1rem', color: theme.palette.text.primary }}>
                  {entry.value?.toFixed(2)}{indicatorConfig?.yAxisLabel}
                </strong>
              </Typography>
            </Box>
          ))}
        </Box>
      );
    }
    return null;
  };

  // Legenda customizada clicavel
  const renderLegend = () => {
    if (levelConfig.isLeaf) return null;

    return (
      <Stack
        direction="row"
        flexWrap="wrap"
        justifyContent="center"
        gap={1}
        sx={{ mt: 2, px: 2 }}
      >
        {items.map((item) => (
          <Chip
            key={item}
            label={item}
            size="medium"
            onClick={() => handleDrillDown(item)}
            onDelete={() => handleLegendClick(item)}
            deleteIcon={
              <Box
                sx={{
                  width: 20,
                  height: 20,
                  borderRadius: '50%',
                  backgroundColor: hiddenItems[item] ? 'grey.400' : 'transparent',
                  border: '2px solid',
                  borderColor: hiddenItems[item] ? 'grey.400' : 'rgba(255,255,255,0.5)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: 12,
                  fontWeight: 'bold'
                }}
              >
                {hiddenItems[item] ? '' : '✓'}
              </Box>
            }
            sx={{
              backgroundColor: hiddenItems[item] ? 'grey.200' : colorMap[item],
              color: hiddenItems[item] ? 'text.secondary' : 'white',
              opacity: hiddenItems[item] ? 0.5 : 1,
              cursor: 'pointer',
              fontSize: '0.85rem',
              fontWeight: 500,
              height: 32,
              px: 1,
              '& .MuiChip-deleteIcon': {
                color: 'inherit'
              },
              '&:hover': {
                opacity: 0.85,
                transform: 'scale(1.03)',
                boxShadow: 2
              }
            }}
          />
        ))}
      </Stack>
    );
  };

  return (
    <Card sx={{ height: '100%' }}>
      <CardContent>
        {/* Header com Breadcrumb e Controles */}
        <Box sx={{ mb: 2 }}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={8}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                {drillPath.length > 1 && (
                  <Button
                    size="medium"
                    variant="outlined"
                    startIcon={<ArrowBackIcon />}
                    onClick={handleBack}
                    sx={{ mr: 1 }}
                  >
                    Voltar
                  </Button>
                )}
                <Breadcrumbs separator={<NavigateNextIcon />} sx={{ flexGrow: 1 }}>
                  {drillPath.map((item, index) => (
                    <Link
                      key={index}
                      component="button"
                      variant="body1"
                      onClick={() => handleBreadcrumbClick(index)}
                      underline={index < drillPath.length - 1 ? 'hover' : 'none'}
                      sx={{
                        cursor: index < drillPath.length - 1 ? 'pointer' : 'default',
                        fontWeight: index === drillPath.length - 1 ? 600 : 400,
                        fontSize: '1rem',
                        color: index === drillPath.length - 1 ? 'primary.main' : 'text.secondary',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 0.5
                      }}
                    >
                      {index === 0 && <HomeIcon />}
                      {item.label}
                    </Link>
                  ))}
                </Breadcrumbs>
              </Box>
            </Grid>
            <Grid item xs={12} md={4}>
              <FormControl fullWidth>
                <InputLabel>Indicador</InputLabel>
                <Select
                  value={selectedIndicator}
                  onChange={(e) => setSelectedIndicator(e.target.value)}
                  label="Indicador"
                >
                  {INDICATORS.map(ind => (
                    <MenuItem key={ind.key} value={ind.key}>{ind.label}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </Box>

        {/* Titulo do nivel atual */}
        <Typography
          variant={levelConfig.isLeaf ? 'h5' : 'h6'}
          sx={{ mb: 1.5, fontWeight: 600 }}
        >
          {indicatorConfig?.label} por {levelConfig.name}
          {levelConfig.isLeaf && drillPath.length > 1 && (
            <Typography component="span" variant="body1" color="text.secondary" sx={{ ml: 1.5 }}>
              ({drillPath[drillPath.length - 1].label})
            </Typography>
          )}
        </Typography>

        {/* Instrucao de uso */}
        {!levelConfig.isLeaf && (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2, display: 'block' }}>
            Clique em um item da legenda para ver detalhes do proximo nivel
          </Typography>
        )}

        {/* Controles de Zoom (apenas para nivel diario) */}
        {levelConfig.isLeaf && (
          <Paper elevation={1} sx={{ p: 2, mb: 2, display: 'flex', alignItems: 'center', gap: 2.5, flexWrap: 'wrap' }}>
            <Typography variant="body1" sx={{ fontWeight: 600, minWidth: 60 }}>
              Zoom:
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <MuiTooltip title="Diminuir zoom">
                <span>
                  <IconButton
                    size="small"
                    onClick={handleZoomOut}
                    disabled={zoomLevel <= 100}
                    color="primary"
                  >
                    <ZoomOutIcon />
                  </IconButton>
                </span>
              </MuiTooltip>
              <Slider
                value={zoomLevel}
                onChange={(e, val) => setZoomLevel(val)}
                min={100}
                max={300}
                step={25}
                sx={{ width: 150 }}
                valueLabelDisplay="auto"
                valueLabelFormat={(v) => `${v}%`}
              />
              <MuiTooltip title="Aumentar zoom">
                <IconButton
                  size="small"
                  onClick={handleZoomIn}
                  disabled={zoomLevel >= 300}
                  color="primary"
                >
                  <ZoomInIcon />
                </IconButton>
              </MuiTooltip>
              <MuiTooltip title="Resetar zoom">
                <IconButton
                  size="small"
                  onClick={handleZoomReset}
                  color="secondary"
                >
                  <RestartAltIcon />
                </IconButton>
              </MuiTooltip>
            </Box>
            <Typography variant="body2" color="text.secondary">
              {zoomLevel}% | Use a barra de rolagem horizontal para navegar
            </Typography>
            {averageValue !== null && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, ml: 'auto' }}>
                <Box
                  sx={{
                    width: 35,
                    height: 0,
                    borderTop: '3px dashed #ff5722'
                  }}
                />
                <Typography variant="body1" sx={{ color: '#ff5722', fontWeight: 600 }}>
                  Média: {averageValue.toFixed(2)}{indicatorConfig?.yAxisLabel}
                </Typography>
              </Box>
            )}
          </Paper>
        )}

        {/* Loading */}
        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: height }}>
            <CircularProgress />
          </Box>
        )}

        {/* Erro */}
        {error && (
          <Paper sx={{ p: 2, backgroundColor: 'error.light', color: 'error.contrastText' }}>
            <Typography>{error}</Typography>
          </Paper>
        )}

        {/* Sem dados */}
        {!loading && !error && chartData.length === 0 && (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: height }}>
            <Typography color="text.secondary">Sem dados para exibir</Typography>
          </Box>
        )}

        {/* Grafico */}
        {!loading && !error && chartData.length > 0 && (
          <>
            {/* Container com scroll horizontal para zoom */}
            <Box
              sx={{
                height: levelConfig.isLeaf ? height + 20 : height,
                width: '100%',
                overflowX: levelConfig.isLeaf && zoomLevel > 100 ? 'auto' : 'hidden',
                overflowY: 'hidden',
                '&::-webkit-scrollbar': {
                  height: 10
                },
                '&::-webkit-scrollbar-track': {
                  backgroundColor: theme.palette.grey[200],
                  borderRadius: 5
                },
                '&::-webkit-scrollbar-thumb': {
                  backgroundColor: theme.palette.primary.light,
                  borderRadius: 5,
                  '&:hover': {
                    backgroundColor: theme.palette.primary.main
                  }
                }
              }}
            >
              <Box
                sx={{
                  height: height,
                  width: levelConfig.isLeaf ? `${zoomLevel}%` : '100%',
                  minWidth: levelConfig.isLeaf ? '100%' : undefined
                }}
              >
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart
                    data={chartData}
                    margin={{ top: 10, right: 30, left: 25, bottom: levelConfig.isLeaf ? 25 : 10 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" stroke={theme.palette.divider} />
                    <XAxis
                      dataKey={levelConfig.isLeaf ? 'dta_label' : 'mes_label'}
                      tick={{
                        fill: theme.palette.text.secondary,
                        fontSize: levelConfig.isLeaf ? 14 : 13
                      }}
                      stroke={theme.palette.divider}
                      angle={levelConfig.isLeaf ? -45 : 0}
                      textAnchor={levelConfig.isLeaf ? 'end' : 'middle'}
                      height={levelConfig.isLeaf ? 70 : 35}
                      interval={levelConfig.isLeaf ? 0 : 'preserveStartEnd'}
                    />
                    <YAxis
                      tick={{
                        fill: theme.palette.text.secondary,
                        fontSize: levelConfig.isLeaf ? 14 : 13
                      }}
                      stroke={theme.palette.divider}
                      label={{
                        value: indicatorConfig?.yAxisLabel || '',
                        angle: -90,
                        position: 'insideLeft',
                        style: {
                          fill: theme.palette.text.secondary,
                          fontSize: levelConfig.isLeaf ? 15 : 14
                        }
                      }}
                      width={levelConfig.isLeaf ? 65 : 55}
                    />
                    <Tooltip content={<CustomTooltip />} />
                    <Legend content={() => null} />

                    {/* Linha de media (apenas para nivel diario) */}
                    {levelConfig.isLeaf && averageValue !== null && (
                      <ReferenceLine
                        y={averageValue}
                        stroke="#ff5722"
                        strokeDasharray="10 5"
                        strokeWidth={2.5}
                        label={{
                          value: `Média: ${averageValue.toFixed(2)}${indicatorConfig?.yAxisLabel || ''}`,
                          position: 'insideTopRight',
                          fill: '#ff5722',
                          fontSize: 15,
                          fontWeight: 700
                        }}
                      />
                    )}

                    {levelConfig.isLeaf ? (
                      // Para nivel diario, uma linha so com pontos maiores
                      <Line
                        type="monotone"
                        dataKey={selectedIndicator}
                        name="Valor"
                        stroke={COLORS[0]}
                        strokeWidth={3.5}
                        dot={{ r: 6, strokeWidth: 2 }}
                        activeDot={{ r: 10, strokeWidth: 2 }}
                        connectNulls
                      />
                    ) : (
                      // Para outros niveis, uma linha por item
                      items.map((item) => (
                        <Line
                          key={item}
                          type="monotone"
                          dataKey={item}
                          name={item}
                          stroke={colorMap[item]}
                          strokeWidth={2.5}
                          dot={{ r: 4 }}
                          activeDot={{ r: 7 }}
                          hide={hiddenItems[item]}
                          connectNulls
                        />
                      ))
                    )}
                  </LineChart>
                </ResponsiveContainer>
              </Box>
            </Box>
            {renderLegend()}
          </>
        )}
      </CardContent>
    </Card>
  );
};

export default DrillDownChart;
